import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { toast } from 'sonner'
import { Contact, Pencil, Plus, Search, Trash2 } from 'lucide-react'
import {
  createEmpresaContacto,
  deleteEmpresaContacto,
  fetchEmpresa,
  fetchEmpresaContactos,
  updateEmpresaContacto,
  type EmpresaContact,
} from '../../api'
import { useAuth } from '../../AuthContext'
import { PageHeader } from '../../components/PageHeader'
import { UserAvatar } from '../../components/UserAvatar'
import { Card } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/skeleton'
import { Pagination } from '../../components/ui/pagination'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog'
import { timeAgo } from '../../utils/time'

const PAGE_SIZE = 8
const emptyCreateForm = { sociedad_id: '', phone: '', display_name: '' }
const emptyEditForm = { phone: '', display_name: '' }

function matches(query: string, ...fields: string[]): boolean {
  if (!query.trim()) return true
  const q = query.trim().toLowerCase()
  return fields.some((f) => f.toLowerCase().includes(q))
}

export function EmpresaContactosPage() {
  const { user, activeSociedadId } = useAuth()
  const empresaId = user?.sociedades.find((s) => s.id === activeSociedadId)?.empresa_id
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [sociedadFilter, setSociedadFilter] = useState('all')
  const [page, setPage] = useState(1)

  const [createOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState(emptyCreateForm)
  const [createError, setCreateError] = useState<string | null>(null)

  const [editTarget, setEditTarget] = useState<EmpresaContact | null>(null)
  const [editForm, setEditForm] = useState(emptyEditForm)
  const [editError, setEditError] = useState<string | null>(null)

  const [deleteTarget, setDeleteTarget] = useState<EmpresaContact | null>(null)

  const empresaQuery = useQuery({
    queryKey: ['empresa', empresaId],
    queryFn: () => fetchEmpresa(empresaId!),
    enabled: !!empresaId,
  })

  const contactosQuery = useQuery({
    queryKey: ['empresa-contactos', empresaId],
    queryFn: () => fetchEmpresaContactos(empresaId!),
    enabled: !!empresaId,
  })

  function invalidate(): void {
    queryClient.invalidateQueries({ queryKey: ['empresa-contactos', empresaId] })
  }

  const createMutation = useMutation({
    mutationFn: () => createEmpresaContacto(empresaId!, createForm),
    onSuccess: () => {
      invalidate()
      setCreateOpen(false)
      setCreateForm(emptyCreateForm)
      toast.success('Contacto creado.')
    },
    onError: (err) => {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setCreateError('Ya existe un contacto con ese teléfono en esa Sociedad.')
      } else {
        setCreateError('No se pudo crear el contacto. Revisá los datos e intentá de nuevo.')
      }
    },
  })

  const updateMutation = useMutation({
    mutationFn: () => updateEmpresaContacto(empresaId!, editTarget!.id, editForm),
    onSuccess: () => {
      invalidate()
      setEditTarget(null)
      toast.success('Contacto actualizado.')
    },
    onError: (err) => {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setEditError('Ya existe un contacto con ese teléfono en esa Sociedad.')
      } else {
        setEditError('No se pudo guardar. Revisá los datos e intentá de nuevo.')
      }
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteEmpresaContacto(empresaId!, deleteTarget!.id),
    onSuccess: () => {
      invalidate()
      setDeleteTarget(null)
      toast.success('Contacto eliminado.')
    },
    onError: () => toast.error('No se pudo eliminar el contacto.'),
  })

  useEffect(() => {
    if (editTarget) {
      setEditForm({ phone: editTarget.phone, display_name: editTarget.display_name })
      setEditError(null)
    }
  }, [editTarget])

  const filtered = useMemo(
    () =>
      (contactosQuery.data ?? [])
        .filter((c) => sociedadFilter === 'all' || c.sociedad_id === sociedadFilter)
        .filter((c) => matches(search, c.display_name, c.phone, c.sociedad_nombre)),
    [contactosQuery.data, search, sociedadFilter],
  )
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  function handleCreateSubmit(e: FormEvent): void {
    e.preventDefault()
    setCreateError(null)
    createMutation.mutate()
  }

  function handleEditSubmit(e: FormEvent): void {
    e.preventDefault()
    setEditError(null)
    updateMutation.mutate()
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={Contact}
        title="Contactos"
        description="Todos los clientes que le escribieron al bot, en todas tus Sociedades."
      />

      <Card className="overflow-hidden py-0">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
          <p className="text-sm text-muted-foreground">
            {contactosQuery.data ? `${contactosQuery.data.length} contactos en total` : 'Cargando…'}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={sociedadFilter}
              onValueChange={(v) => {
                setSociedadFilter(v)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Sociedad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las Sociedades</SelectItem>
                {empresaQuery.data?.sociedades.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="relative">
              <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre, teléfono o Sociedad…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setPage(1)
                }}
                className="w-72 pl-8"
              />
            </div>

            <Dialog open={createOpen} onOpenChange={(next) => { setCreateOpen(next); if (!next) { setCreateForm(emptyCreateForm); setCreateError(null) } }}>
              <DialogTrigger asChild>
                <Button disabled={!empresaQuery.data?.sociedades.length}>
                  <Plus className="size-4" />
                  Nuevo contacto
                </Button>
              </DialogTrigger>
              <DialogContent>
                <form onSubmit={handleCreateSubmit} className="flex flex-col gap-4">
                  <DialogHeader>
                    <DialogTitle>Nuevo contacto</DialogTitle>
                  </DialogHeader>
                  <div className="flex flex-col gap-1.5">
                    <Label>Sociedad</Label>
                    <Select value={createForm.sociedad_id} onValueChange={(v) => setCreateForm({ ...createForm, sociedad_id: v })}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Elegí una Sociedad" />
                      </SelectTrigger>
                      <SelectContent>
                        {empresaQuery.data?.sociedades.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="contact-phone">Teléfono</Label>
                    <Input
                      id="contact-phone"
                      required
                      placeholder="+50600000000"
                      value={createForm.phone}
                      onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="contact-nombre">Nombre (opcional)</Label>
                    <Input
                      id="contact-nombre"
                      value={createForm.display_name}
                      onChange={(e) => setCreateForm({ ...createForm, display_name: e.target.value })}
                    />
                  </div>

                  {createError && <p className="text-sm text-destructive">{createError}</p>}

                  <DialogFooter>
                    <Button type="submit" disabled={createMutation.isPending || !createForm.sociedad_id || !createForm.phone}>
                      <Plus className="size-4" />
                      Crear contacto
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {contactosQuery.isLoading && (
          <div className="flex flex-col gap-3 p-5">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-5/6" />
          </div>
        )}
        {contactosQuery.isError && <p className="p-5 text-sm text-destructive">No se pudieron cargar los contactos.</p>}
        {contactosQuery.data && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
            <Contact className="size-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              {search ? 'Ningún contacto coincide con la búsqueda.' : 'Todavía no hay contactos.'}
            </p>
          </div>
        )}
        {paged.length > 0 && (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contacto</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>Sociedad</TableHead>
                  <TableHead>Última actividad</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paged.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2.5">
                        <UserAvatar name={c.display_name || c.phone} seed={c.phone} size="sm" />
                        {c.display_name || <span className="text-muted-foreground">Sin nombre</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{c.phone}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{c.sociedad_nombre}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {c.last_inbound_at ? timeAgo(c.last_inbound_at) : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setEditTarget(c)}>
                          <Pencil className="size-3.5" />
                          Editar
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(c)} title="Eliminar">
                          <Trash2 className="size-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination page={page} pageCount={pageCount} totalItems={filtered.length} pageSize={PAGE_SIZE} onPageChange={setPage} />
          </>
        )}
      </Card>

      <Dialog open={!!editTarget} onOpenChange={(next) => !next && setEditTarget(null)}>
        <DialogContent>
          <form onSubmit={handleEditSubmit} className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle>Editar contacto</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-phone">Teléfono</Label>
              <Input
                id="edit-phone"
                required
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-nombre">Nombre</Label>
              <Input
                id="edit-nombre"
                value={editForm.display_name}
                onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
              />
            </div>

            {editError && <p className="text-sm text-destructive">{editError}</p>}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditTarget(null)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                Guardar cambios
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(next) => !next && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar contacto</DialogTitle>
            <DialogDescription>
              Esto borra a <strong>{deleteTarget?.display_name || deleteTarget?.phone}</strong> junto con{' '}
              <strong>todas sus conversaciones y mensajes</strong>. No se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" disabled={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>
              Sí, eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
